export type TreatmentPlanPrintDocumentKey = 'TREATMENT_PLAN' | 'TREATMENT_PLAN_FORM'

export interface TreatmentPlanPrintHistoryPayload {
  documentKey: TreatmentPlanPrintDocumentKey
  branchId: number
  patientId: number
  patientCaseId: number
  documentName: string
  documentNameEn: string
  jsonData: string
  url: string
}

export interface TreatmentPlanPrintHistorySnapshot {
  plan: any
  patient: any
  doctors: any[]
}

export interface TreatmentPlanPrintSignatureState {
  signaturesByDoctor: Record<number, { doctor: string, patient: string }>
  signedAtByDoctor?: Record<number, { doctor: string, patient: string }>
  savedDoctorIds: number[]
}

export const createTreatmentPlanPrintHistoryDeduper = (windowMs = 1000) => {
  const lastPrintedAt = new Map<string, number>()

  return (documentKey: TreatmentPlanPrintDocumentKey, plan: any, now = Date.now()): boolean => {
    const planId = Number(plan?.patientTreatmentPlanId || 0)
    const planKey = planId > 0
      ? `id:${planId}`
      : `case:${plan?.patientCaseId || ''}:name:${plan?.treatmentPlanName || ''}`
    const key = `${documentKey}:${planKey}`
    const previousPrintAt = lastPrintedAt.get(key)

    if (previousPrintAt !== undefined && now - previousPrintAt < windowMs) return false

    lastPrintedAt.set(key, now)
    return true
  }
}

export const getTreatmentPlanFormActionVisibility = ({
  isReprint = false,
  isActiveDoctorSaved,
  hasAnySaved,
}: {
  isReprint?: boolean
  isActiveDoctorSaved: boolean
  hasAnySaved: boolean
}) => ({
  showSave: !isReprint && !isActiveDoctorSaved,
  showCancelDocument: !isReprint && hasAnySaved,
})

type TreatmentPlanPrintHistoryInput = {
  documentKey: TreatmentPlanPrintDocumentKey
  branchId: number
  plan: any
  patient: any
  doctors: any[]
  signatureState?: TreatmentPlanPrintSignatureState
}

export const parseTreatmentPlanValueMap = (value?: unknown): Record<string, string> => {
  if (!value) return {}

  if (typeof value === 'object' && !Array.isArray(value)) return value as Record<string, string>
  if (typeof value !== 'string') return {}

  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

export const getTreatmentPlanValueForDoctor = (value: unknown, doctorId?: number | string): string => {
  const key = String(doctorId ?? '')
  const valueMap = parseTreatmentPlanValueMap(value)
  if (key && valueMap[key]) return valueMap[key]

  // Older API responses may contain a scalar timestamp instead of a per-doctor map.
  if (
    typeof value === 'string'
    && value.trim()
    && !value.trim().startsWith('{')
    && !value.trim().startsWith('[')
    && !Number.isNaN(Date.parse(value))
  ) {
    return value
  }

  return ''
}

const parseSignatures = (value?: string | null): Record<string, string> => parseTreatmentPlanValueMap(value)

const getPlanSnapshotWithSavedSignatures = (plan: any, signatureState?: TreatmentPlanPrintSignatureState) => {
  if (!signatureState) return plan

  const doctorSignatures = parseSignatures(plan?.doctorSignature)
  const patientSignatures = parseSignatures(plan?.patientSignature)
  const doctorSignedAt = parseTreatmentPlanValueMap(plan?.doctorSignedAt)
  const patientSignedAt = parseTreatmentPlanValueMap(plan?.patientSignedAt)

  signatureState.savedDoctorIds.forEach((doctorId) => {
    const key = String(doctorId)
    const signatures = signatureState.signaturesByDoctor[doctorId]
    if (!signatures?.doctor || !signatures?.patient) return

    doctorSignatures[key] = signatures.doctor
    patientSignatures[key] = signatures.patient

    const signedAt = signatureState.signedAtByDoctor?.[doctorId]
    if (signedAt?.doctor) doctorSignedAt[key] = signedAt.doctor
    if (signedAt?.patient) patientSignedAt[key] = signedAt.patient
  })

  return {
    ...plan,
    doctorSignature: JSON.stringify(doctorSignatures),
    patientSignature: JSON.stringify(patientSignatures),
    doctorSignedAt: Object.keys(doctorSignedAt).length ? JSON.stringify(doctorSignedAt) : plan?.doctorSignedAt || null,
    patientSignedAt: Object.keys(patientSignedAt).length ? JSON.stringify(patientSignedAt) : plan?.patientSignedAt || null,
  }
}

const documentLabels: Record<TreatmentPlanPrintDocumentKey, Pick<TreatmentPlanPrintHistoryPayload, 'documentName' | 'documentNameEn'>> = {
  TREATMENT_PLAN: {
    documentName: 'แผนการรักษา',
    documentNameEn: 'Treatment plan',
  },
  TREATMENT_PLAN_FORM: {
    documentName: 'ใบแผนการรักษา',
    documentNameEn: 'Treatment plan form',
  },
}

export const buildTreatmentPlanPrintHistoryPayload = ({
  documentKey,
  branchId,
  plan,
  patient,
  doctors,
  signatureState,
}: TreatmentPlanPrintHistoryInput): TreatmentPlanPrintHistoryPayload => ({
  documentKey,
  branchId: Number(branchId),
  patientId: Number(patient?.patientId || 0),
  patientCaseId: Number(plan?.patientCaseId || patient?.patientCase?.patientCaseId || patient?.patientCaseId || 0),
  ...documentLabels[documentKey],
  jsonData: JSON.stringify({ plan: getPlanSnapshotWithSavedSignatures(plan, signatureState), patient, doctors }),
  // Treatment plan documents are rendered in the browser and do not have a server PDF URL.
  url: '',
})

export const isClientRenderedTreatmentPlanDocument = (documentKey?: string): boolean => (
  documentKey === 'TREATMENT_PLAN' || documentKey === 'TREATMENT_PLAN_FORM'
)

export const parseTreatmentPlanPrintHistoryData = (
  jsonData?: string | null,
): TreatmentPlanPrintHistorySnapshot | null => {
  if (!jsonData) return null

  try {
    const snapshot = JSON.parse(jsonData)
    if (!snapshot || typeof snapshot !== 'object' || !snapshot.plan || !snapshot.patient) return null

    return {
      plan: snapshot.plan,
      patient: snapshot.patient,
      doctors: Array.isArray(snapshot.doctors) ? snapshot.doctors : [],
    }
  } catch {
    return null
  }
}
